import {List, ListItem, ListItemText, MenuItem, Select, SelectChangeEvent, SvgIcon} from '@mui/material';
import * as Sentry from '@sentry/react';
import {useTolgee, useTranslation} from 'tolgee';

import {useUserPreferencesMutation} from '~api/user.mutations';
import {selectLandingPage, selectUserEmail} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

import {ABOVE_ASK_AI_PANEL_Z_INDEX} from '~components/ask-ai/askAiAgents';
import {styled} from 'src/constants/theme';
import {FlagDeIcon, FlagEnIcon} from 'src/icons';
import {StatusWrapper} from '../common/StatusWrapper';

const LanguageItem = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexGrow: 1,
});

const MenuProps = {
  sx: {zIndex: ABOVE_ASK_AI_PANEL_Z_INDEX},
  'data-testid': 'personal-tab-select-menu',
  style: {
    padding: 32,
  },
  PaperProps: {
    style: {
      maxHeight: 264,
      width: 370,
    },
  },
};

const LanguageIcon = ({language}: {language: string}): JSX.Element => {
  const languageIdToLanguage: {[key: string]: string} = {'de-DE': 'Deutsch', en: 'English'};
  const icon = () => {
    switch (language) {
      case 'de-DE':
        return FlagDeIcon;
      case 'en':
        return FlagEnIcon;
      default:
        return FlagEnIcon;
    }
  };

  return (
    <LanguageItem data-testid={'languageitem-' + language}>
      <SvgIcon component={icon()} name={language} height={20} />
      {languageIdToLanguage[language]}
    </LanguageItem>
  );
};

const PersonalListItem = ({primary, secondary}: {primary: string; secondary?: string | number}): JSX.Element => {
  return (
    <ListItem>
      <ListItemText
        primary={primary}
        secondary={secondary}
        primaryTypographyProps={{fontSize: 16}}
        secondaryTypographyProps={{fontSize: 14, fontWeight: 600, color: 'maddox.black80'}}
      />
    </ListItem>
  );
};

export function PersonalTab(): JSX.Element {
  const {t} = useTranslation(['user', 'mainMenu']);
  const tolgee = useTolgee(['language']);
  const userEmail = useAppSelector(selectUserEmail);
  const userLandingPage = useAppSelector(selectLandingPage);
  const setUserPreferences = useUserPreferencesMutation();

  const language = tolgee.getLanguage();

  const updateLanguage = async (event: SelectChangeEvent): Promise<void> => {
    tolgee.changeLanguage(event.target.value);
    Sentry.setTag('language', event.target.value);
    localStorage.setItem('i18nextLng', event.target.value);
  };

  const updateStartPage = async (event: SelectChangeEvent): Promise<void> => {
    setUserPreferences.mutate(event.target.value);
    Sentry.setTag('startPage', event.target.value);
    localStorage.setItem('startPage', event.target.value);
  };

  return (
    <StatusWrapper
      isLoading={!userEmail}
      creator={(): JSX.Element => (
        <List>
          <PersonalListItem primary={t('language')} />
          <ListItem sx={{marginTop: -2}}>
            <Select
              id='language-select'
              data-testid='language-select'
              size='small'
              autoWidth
              value={language}
              onChange={updateLanguage}
              sx={{width: '100%'}}
              MenuProps={MenuProps}
            >
              <MenuItem value='de-DE'>
                <LanguageIcon language='de-DE' />
              </MenuItem>
              <MenuItem value='en'>
                <LanguageIcon language='en' />
              </MenuItem>
            </Select>
          </ListItem>
          <PersonalListItem primary={t('startPage')} />
          <ListItem sx={{marginTop: -2}}>
            <Select
              id='route-select'
              data-testid='route-select'
              size='small'
              autoWidth
              defaultValue={userLandingPage}
              onChange={updateStartPage}
              sx={{width: '100%'}}
              MenuProps={MenuProps}
            >
              <MenuItem value='MONITOR'>{t('monitor', {ns: 'mainMenu'})}</MenuItem>
              <MenuItem value='MODELS'>{t('models', {ns: 'mainMenu'})}</MenuItem>
              <MenuItem value='TASKS'>{t('tasks', {ns: 'mainMenu'})}</MenuItem>
              <MenuItem value='ARCHIVE'>{t('archive', {ns: 'mainMenu'})}</MenuItem>
            </Select>
          </ListItem>
        </List>
      )}
    />
  );
}
